import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from '../../Hooks/useBackend';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { WorkoutLog } from '../WorkoutLog/WorkoutLog';
import { ActivityLogSource } from './ActivityLogSource';
import { Activity } from './Activity';
import dayjs from 'dayjs';
import { RouteLocation } from './RouteLocation';
import { Pageable } from '../Interfaces/Pageable';
import { ExploreEntry } from '../Explore/ExploreEntry';
import { Audited, createAuditedFields } from '../Audited';
import { v4 as UUID } from 'uuid';

export type ActivityLogQueryRequest = Pageable & {
  athleteId?: string;
  type?: string;
  source?: string;
  sourceType?: string;
  sourceId?: string;
  startDate?: Date;
  endDate?: Date;
  excludedSourceType?: string;
  excludedSourceId?: string[];
};

// Plain data type - no MobX, no computed getters, no methods
export type ActivityLog = Audited & {
  athleteId?: string;
  type?: string;
  activityId?: string;
  source: ActivityLogSource;
  startDate: string;
  endDate: string;
  data: Record<string, number>;
  linkedData: Record<string, any>;
  route: RouteLocation[];
  workoutLog?: WorkoutLog;
  activity?: Activity;
  exploreEntry?: ExploreEntry;
};

// Helper to create a plain ActivityLog from partial JSON
export function newActivityLog(json?: Partial<ActivityLog>): ActivityLog {
  return {
    ...createAuditedFields(json),
    athleteId: json?.athleteId,
    type: json?.type,
    activityId: json?.activityId,
    source: json?.source ?? { source: 'kinastic', sourceType: 'activity', sourceId: UUID() },
    startDate: json?.startDate ?? new Date().toISOString(),
    endDate: json?.endDate ?? new Date().toISOString(),
    data: json?.data ?? {},
    linkedData: json?.linkedData ?? {},
    route: json?.route ?? [],
    workoutLog: json?.workoutLog,
    activity: json?.activity,
    exploreEntry: json?.exploreEntry,
  };
}

/**
 * @deprecated Use newActivityLog() instead. Kept for backward compatibility during migration.
 */
export const createActivityLog = newActivityLog;

// --- Standalone utility functions ---

export function getActivityLogCalories(log: ActivityLog): number {
  return log.data['CALORIES'] || 0;
}

export function getActivityLogDurationMs(log: ActivityLog): number {
  if (log.workoutLog) {
    return log.workoutLog.totalDuration;
  }
  return dayjs(log.endDate).diff(dayjs(log.startDate));
}

export function getActivityLogDurationInSeconds(log: ActivityLog): number {
  return log.data['DURATION'] || getActivityLogDurationMs(log) / 1000;
}

export function isActivityLogValid(log: ActivityLog): boolean {
  const start = dayjs(log.startDate);
  const end = dayjs(log.endDate);
  if (!end.isBefore(start)) {
    const diff = end.diff(start, 'minute');
    return diff <= 480;
  }
  return false;
}

export function getActivityLogObject(log: ActivityLog): Activity | WorkoutLog | ExploreEntry | undefined {
  if (log.source.sourceType === 'explore-entry') {
    return log.exploreEntry;
  } else if (log.source.sourceType === 'workout' && log.source.source === 'kinastic') {
    return log.workoutLog;
  }
  return log.activity;
}

/**
 * Save an ActivityLog via HttpBackend (for use outside React components).
 * Prefer useSaveActivityLog() hook inside components.
 */
export function saveActivityLogDirect(activityLog: ActivityLog): Promise<ActivityLog> {
  return HttpBackend.post('/activity/tracking', toActivityLogPayload(activityLog)).then((res) => newActivityLog(res));
}

/**
 * Strips an ActivityLog to a plain payload safe for API posting.
 * Excludes non-serializable fields like workoutLog, activity, exploreEntry.
 */
export function toActivityLogPayload(activityLog: ActivityLog): Record<string, any> {
  return {
    id: activityLog.id,
    athleteId: activityLog.athleteId,
    type: activityLog.type,
    activityId: activityLog.activityId,
    source: activityLog.source
      ? { source: activityLog.source.source, sourceId: activityLog.source.sourceId, sourceType: activityLog.source.sourceType }
      : undefined,
    startDate: activityLog.startDate,
    endDate: activityLog.endDate,
    data: { ...activityLog.data },
    linkedData: activityLog.linkedData ? { ...activityLog.linkedData } : {},
    route: activityLog.route ?? [],
  };
}

// Hooks
export function useActivityLogs(params?: Partial<ActivityLogQueryRequest>) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['activityLogs', params],
    queryFn: () => backend.get('/activity/tracking', params).then((res: any) => (res ?? []).map((e: any) => newActivityLog(e))),
    staleTime: 2 * 60 * 1000,
  });
}

export function useActivityLog(id: string | undefined) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['activityLog', id],
    queryFn: () => backend.get(`/activity/tracking/${id}`).then((res: any) => (res ? newActivityLog(res) : undefined)),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSaveActivityLog() {
  const backend = useBackend();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (activityLog: ActivityLog) =>
      backend.post('/activity/tracking', toActivityLogPayload(activityLog)).then((res: any) => {
        return newActivityLog(res);
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] });
    },
  });
}

export function useDeleteActivityLog() {
  const backend = useBackend();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (activityLog: ActivityLog) =>
      backend.delete(`/activity/tracking/${activityLog.id}`).then(() => {
        return activityLog;
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] });
    },
  });
}

// Legacy static API - prefer hooks above
export const ActivityLogApi = {
  find(params?: Partial<ActivityLogQueryRequest>): Promise<ActivityLog[]> {
    return HttpBackend.get('/activity/tracking', params).then((result: any) => result.map((e: any) => newActivityLog(e)));
  },

  get(id: string): Promise<ActivityLog | undefined> {
    return HttpBackend.get(`/activity/tracking/${id}`).then((res: any) => (res ? newActivityLog(res) : undefined));
  },

  fromWorkoutLog(workoutLog: WorkoutLog): ActivityLog {
    return newActivityLog({
      id: `activitylog:${workoutLog.id}`,
      type: workoutLog.type || 'gym_strength',
      startDate: workoutLog.startDateTime?.toISOString(),
      endDate: workoutLog.endDateTime?.toISOString(),
      source: {
        source: 'kinastic',
        sourceId: workoutLog.id,
        sourceType: 'workout',
      },
      data: workoutLog.aggregatedValues,
      workoutLog,
    });
  },
};
